import simpleDDP from 'simpleddp';
import ws from 'isomorphic-ws';
import type { DDPClientOptions } from './types';

/**
 * Thin wrapper around SimpleDDP for calling Meteor methods,
 * subscribing to publications, and CRUD on Meteor collections.
 *
 * - Lazy init: WebSocket doesn't open until first call() or subscribe()
 * - Auth via getToken callback (same pattern as @scio3/graphql-client)
 * - On reconnect: re-calls getToken() → login automatically
 */
export class DDPClient {
  private _options: DDPClientOptions;
  private _client: simpleDDP | null = null;
  private _connectPromise: Promise<void> | null = null;
  private _userId: string | null = null;
  private _currentTeamId: string | null = null;

  constructor(options: DDPClientOptions) {
    this._options = options;
  }

  get userId(): string | null {
    return this._userId;
  }

  // ── Team context ─────────────────────────────────────────────

  /**
   * Sync the active teamId to the Meteor connection.
   * Calls `setCurrentTeamId` on the server so that collection
   * autoValue hooks (insert) use the correct team.
   * Also stored locally so it can be re-sent on reconnect.
   */
  async setCurrentTeamId(teamId: string): Promise<void> {
    if (teamId === this._currentTeamId) return;
    const prev = this._currentTeamId;
    this._currentTeamId = teamId;
    if (this._client && this._userId) {
      try {
        await this._client.call('setCurrentTeamId', teamId);
      } catch (err) {
        this._currentTeamId = prev;
        throw err;
      }
    }
  }

  // ── Method calls ──────────────────────────────────────────────

  async call<T = unknown>(method: string, ...args: unknown[]): Promise<T> {
    await this._ensureReady();
    return this._client!.call(method, ...args) as Promise<T>;
  }

  // ── Subscriptions ─────────────────────────────────────────────

  async subscribe(name: string, ...args: unknown[]) {
    await this._ensureReady();
    return this._client!.subscribe(name, ...args);
  }

  // ── Collection access ─────────────────────────────────────────

  collection<T = unknown>(name: string) {
    if (!this._client) {
      throw new Error('DDPClient: not connected. Call call() or subscribe() first.');
    }
    return this._client.collection(name) as ReturnType<simpleDDP['collection']> & {
      fetch(): T[];
      filter(predicate: (doc: T) => boolean): ReturnType<simpleDDP['collection']>;
    };
  }

  // ── Collection CRUD (allow/deny methods) ──────────────────────

  async collectionInsert<T extends Record<string, unknown>>(
    collectionName: string,
    doc: T,
  ): Promise<string> {
    return this.call<string>(`/${collectionName}/insert`, doc);
  }

  async collectionUpdate(
    collectionName: string,
    id: string,
    modifier: Record<string, unknown>,
  ): Promise<void> {
    return this.call<void>(`/${collectionName}/update`, { _id: id }, modifier);
  }

  async collectionRemove(collectionName: string, id: string): Promise<void> {
    return this.call<void>(`/${collectionName}/remove`, { _id: id });
  }

  // ── Disconnect ────────────────────────────────────────────────

  disconnect(): void {
    if (this._client) {
      this._client.disconnect();
      this._client = null;
      this._connectPromise = null;
      this._userId = null;
      this._currentTeamId = null;
    }
  }

  // ── Internal: lazy connect + auth ─────────────────────────────

  private async _ensureReady(): Promise<void> {
    if (this._client && this._userId) return;
    if (this._connectPromise) return this._connectPromise;

    this._connectPromise = this._connectAndAuth();
    try {
      await this._connectPromise;
    } catch (err) {
      // Reset so next call retries
      this._connectPromise = null;
      throw err;
    }
  }

  private async _connectAndAuth(): Promise<void> {
    this._client = new simpleDDP({
      endpoint: this._options.endpoint,
      SocketConstructor: ws,
      reconnectInterval: this._options.reconnectInterval ?? 5000,
      autoConnect: false,
      autoReconnect: true,
    });

    // Re-authenticate on reconnect (SimpleDDP auto-reconnects)
    this._client.on('connected', () => {
      if (this._userId) {
        // Was previously authenticated — re-auth with fresh token, then re-sync teamId
        this._authenticate()
          .then(() => {
            if (this._currentTeamId) {
              return this._client!.call('setCurrentTeamId', this._currentTeamId);
            }
          })
          .catch((err) => {
            console.error('[DDPClient] re-auth on reconnect failed:', err);
          });
      }
    });

    await this._client.connect();
    await this._authenticate();

    // Sync teamId if it was set before connection was established
    if (this._currentTeamId) {
      await this._client.call('setCurrentTeamId', this._currentTeamId);
    }
  }

  private async _authenticate(): Promise<void> {
    const token = await this._options.getToken();
    if (!token) {
      throw new Error('DDPClient: getToken() returned null — cannot authenticate');
    }

    const result = (await this._client!.call('login', { firebaseToken: token })) as {
      id?: string;
      userId?: string;
    };

    this._userId = result?.id ?? result?.userId ?? null;
  }
}

// ── Factory functions ─────────────────────────────────────────────

let _instance: DDPClient | null = null;
let _singletonOptions: DDPClientOptions | null = null;

/** Create a fresh instance (for tests or multiple connections) */
export function createDDPClient(options: DDPClientOptions): DDPClient {
  return new DDPClient(options);
}

/**
 * Lazy singleton. On first call, requires options.
 * Subsequent calls return the same instance.
 */
export function getDDPClient(options?: DDPClientOptions): DDPClient {
  if (_instance) return _instance;

  const opts = options ?? _singletonOptions;
  if (!opts) {
    throw new Error(
      'DDPClient: call getDDPClient(options) with options on first use, ' +
        'or call initDDPClient(options) first.',
    );
  }

  _instance = new DDPClient(opts);
  return _instance;
}

/** Pre-configure singleton options (call early, e.g. in App.tsx) */
export function initDDPClient(options: DDPClientOptions): void {
  _singletonOptions = options;
}

/** Disconnect + destroy singleton (call on logout) */
export function resetDDPClient(): void {
  _instance?.disconnect();
  _instance = null;
}
